import {
  CHECKPOINTS_STORE,
  getDb,
  promisifyRequest,
  promisifyTxn,
} from "./PdfDb";
import type { CheckpointRecord, PdfDocState } from "./types";

export const BASELINE_SENTINEL = "baseline";

export function makeCheckpointId(
  tabId: string,
  chatId: string,
  messageId: string | null,
): string {
  return `cp:${tabId}:${chatId}:${messageId ?? BASELINE_SENTINEL}`;
}

export async function putCheckpoint(
  tabId: string,
  chatId: string,
  messageId: string | null,
  docState: PdfDocState,
): Promise<void> {
  const db = await getDb();
  const txn = db.transaction(CHECKPOINTS_STORE, "readwrite");
  const record: CheckpointRecord = {
    id: makeCheckpointId(tabId, chatId, messageId),
    tabId,
    chatId,
    messageId,
    docState,
    createdAt: Date.now(),
  };
  const completion = promisifyTxn(txn);
  const store = txn.objectStore(CHECKPOINTS_STORE);
  const existing = await promisifyRequest(store.get(record.id));
  if (!existing) store.add(record);
  await completion;
}

export async function getCheckpoint(
  tabId: string,
  chatId: string,
  messageId: string | null,
): Promise<CheckpointRecord | undefined> {
  const db = await getDb();
  const txn = db.transaction(CHECKPOINTS_STORE, "readonly");
  return promisifyRequest(
    txn.objectStore(CHECKPOINTS_STORE).get(
      makeCheckpointId(tabId, chatId, messageId),
    ),
  );
}

export async function listCheckpointsByChat(
  tabId: string,
  chatId: string,
): Promise<CheckpointRecord[]> {
  const db = await getDb();
  const txn = db.transaction(CHECKPOINTS_STORE, "readonly");
  const index = txn.objectStore(CHECKPOINTS_STORE).index("by_tab_chat");
  return promisifyRequest(index.getAll(IDBKeyRange.only([tabId, chatId])));
}

export async function deleteCheckpointsByChat(
  tabId: string,
  chatId: string,
): Promise<void> {
  const db = await getDb();
  const txn = db.transaction(CHECKPOINTS_STORE, "readwrite");
  const store = txn.objectStore(CHECKPOINTS_STORE);
  const index = store.index("by_tab_chat");
  const keys = await promisifyRequest(
    index.getAllKeys(IDBKeyRange.only([tabId, chatId])),
  );
  for (const key of keys) store.delete(key as IDBValidKey);
  await promisifyTxn(txn);
}

export async function deleteCheckpointsByTab(tabId: string): Promise<void> {
  const db = await getDb();
  const txn = db.transaction(CHECKPOINTS_STORE, "readwrite");
  const store = txn.objectStore(CHECKPOINTS_STORE);
  const index = store.index("by_tab");
  const keys = await promisifyRequest(
    index.getAllKeys(IDBKeyRange.only(tabId)),
  );
  for (const key of keys) store.delete(key as IDBValidKey);
  await promisifyTxn(txn);
}
